import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { ptBR, enUS, es as esLocale } from "date-fns/locale";
import type { ProposalLocale, TranslationDictionary } from "./types";
import { SUPPORTED_LOCALES } from "./types";
import { pt } from "./pt";
import { en } from "./en";
import { es } from "./es";

const STORAGE_KEY = "proposal-locale";

const dictionaries: Record<ProposalLocale, TranslationDictionary> = {
  pt,
  en,
  es,
};

const dateLocales = {
  pt: ptBR,
  en: enUS,
  es: esLocale,
};

function resolveInitialLocale(paramValue: string | null): ProposalLocale {
  if (paramValue && SUPPORTED_LOCALES.includes(paramValue as ProposalLocale)) {
    return paramValue as ProposalLocale;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as ProposalLocale)) {
      return stored as ProposalLocale;
    }
  } catch {
    // ignore localStorage errors
  }
  return "pt";
}

interface ProposalI18nContextValue {
  locale: ProposalLocale;
  setLocale: (locale: ProposalLocale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  getDateLocale: () => typeof ptBR;
}

const ProposalI18nContext = createContext<ProposalI18nContextValue | null>(
  null,
);

export function ProposalI18nProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get("lang");

  const [locale, setLocaleState] = useState<ProposalLocale>(() =>
    resolveInitialLocale(langParam),
  );

  const setLocale = useCallback((newLocale: ProposalLocale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch {
      // ignore localStorage errors
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      let text = dictionaries[locale][key] ?? dictionaries.pt[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
        }
      }
      return text;
    },
    [locale],
  );

  const getDateLocale = useCallback(() => dateLocales[locale], [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t, getDateLocale }),
    [locale, setLocale, t, getDateLocale],
  );

  return (
    <ProposalI18nContext.Provider value={value}>
      {children}
    </ProposalI18nContext.Provider>
  );
}

export function useProposalT() {
  const ctx = useContext(ProposalI18nContext);
  if (!ctx) {
    throw new Error("useProposalT must be used within ProposalI18nProvider");
  }
  return ctx;
}

export function formatProposalPrice(value: number): string {
  return `R$ ${value.toLocaleString("pt-BR")}`;
}

export function formatProposalNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return value.toLocaleString("pt-BR", options);
}
